import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField, IconButton, InputAdornment } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Colors from '../../library/Colors';

// Shared building blocks for the webshop-admin screens, so they stop
// re-declaring (and drifting on) the same tokens/components. Keeps the existing
// hex-token approach (deliberately not the big theme-token migration) but in
// ONE place, so every screen resolves the same values.

const hexToRgba = (hex, a) => {
    const h = String(hex).replace('#', '');
    const n = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    const r = parseInt(n.slice(0, 2), 16), g = parseInt(n.slice(2, 4), 16), b = parseInt(n.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
};

// The token block that used to live (verbatim) in every screen.
export const webshopTokens = (theme) => {
    const isDark = theme === 'dark';
    return {
        isDark,
        cardBg: isDark ? '#2d333b' : '#ffffff',
        borderColor: isDark ? '#3a3f47' : '#e4e7eb',
        textColor: isDark ? Colors.dark_text : Colors.light_text,
        mutedText: isDark ? '#b0b8c1' : '#768390',
        panelBg: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
    };
};

// Tinted icon "medallion". The background tint is derived from the SAME colour
// as the glyph (brand blue by default), so the two blues no longer clash.
export const iconBoxSx = (isDark, color = Colors.sft_blue) => ({
    width: 36,
    height: 36,
    borderRadius: 1.25,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: hexToRgba(color, isDark ? 0.16 : 0.09),
    color,
    flexShrink: 0,
    '& svg': { fontSize: 20 },
});

// One page-title component: "h4 / 500" heading + muted body2 description, above
// the content card. Optional right-aligned `action` slot.
export const WebshopPageHeader = ({ title, description, theme, action }) => {
    const { textColor, mutedText } = webshopTokens(theme);
    return (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="h4" sx={{ color: textColor, fontWeight: 500, mb: 0.5 }}>{title}</Typography>
                {description && <Typography variant="body2" sx={{ color: mutedText }}>{description}</Typography>}
            </Box>
            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Box>
    );
};
WebshopPageHeader.propTypes = {
    title: PropTypes.node,
    description: PropTypes.node,
    theme: PropTypes.string,
    action: PropTypes.node,
};

// One password field with a show/hide toggle.
export const WebshopPasswordField = ({ label, value, onChange, visible, onToggle, placeholder, size = 'small', fullWidth = true, ...rest }) => (
    <TextField
        label={label}
        size={size}
        fullWidth={fullWidth}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        InputProps={{
            endAdornment: (
                <InputAdornment position="end">
                    <IconButton size="small" onClick={onToggle} edge="end" tabIndex={-1}>
                        {visible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                </InputAdornment>
            ),
        }}
        {...rest}
    />
);
WebshopPasswordField.propTypes = {
    label: PropTypes.node,
    value: PropTypes.any,
    onChange: PropTypes.func,
    visible: PropTypes.bool,
    onToggle: PropTypes.func,
    placeholder: PropTypes.string,
    size: PropTypes.string,
    fullWidth: PropTypes.bool,
};
